import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, AlarmClock, XCircle, Loader2, MoreHorizontal, Flame } from "lucide-react";
import { toast } from "sonner";

import { crmService, emitCrmChanged } from "@/services/crmService";
import { followupTypeLabel, leadTypeLabel, priorityLabel } from "@/config/crmOptions";
import { fmtDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const isoIn = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
};

const priorityClass = (p) => ({ high: "border-red-300 text-red-700", normal: "border-border text-muted-foreground", low: "border-border text-muted-foreground" }[p] || "border-border text-muted-foreground");

export const FollowupItem = ({ followup: f, onChanged }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customDue, setCustomDue] = useState("");

  const pending = f.status === "pending";
  const refresh = () => { emitCrmChanged(); onChanged?.(); };

  const complete = async (connected) => {
    setBusy(true);
    try {
      await crmService.completeFollowup(f.id, connected);
      toast.success(connected ? "Marked complete — contact connected" : "Marked complete");
      setConfirmOpen(false);
      refresh();
    } catch (e) { toast.error(e.message || "Could not complete"); }
    finally { setBusy(false); }
  };

  const reschedule = async (iso) => {
    setBusy(true);
    try {
      await crmService.rescheduleFollowup(f.id, iso);
      toast.success("Follow-up rescheduled");
      setCustomOpen(false);
      refresh();
    } catch (e) { toast.error(e.message || "Could not reschedule"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await crmService.cancelFollowup(f.id);
      toast.success("Follow-up cancelled");
      refresh();
    } catch (e) { toast.error(e.message || "Could not cancel"); }
    finally { setBusy(false); }
  };

  const statusBadge = () => {
    if (f.status === "completed") return <Badge className="gap-1 bg-emerald-600 text-white hover:bg-emerald-600"><CheckCircle2 className="h-3 w-3" /> Completed</Badge>;
    if (f.status === "cancelled") return <Badge variant="outline" className="text-muted-foreground">Cancelled</Badge>;
    if (f.is_overdue) return <Badge className="gap-1 bg-red-600 text-white hover:bg-red-600"><AlarmClock className="h-3 w-3" /> Overdue</Badge>;
    return <Badge variant="outline" className="gap-1 border-brand-navy/30 text-brand-navy"><Clock className="h-3 w-3" /> Pending</Badge>;
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`followup-item-${f.id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <button className="font-medium text-foreground hover:text-accent" onClick={() => f.contact?.id && navigate(`/crm/${f.contact.id}`)} data-testid={`followup-contact-${f.id}`}>
            {f.contact?.first_name} {f.contact?.last_name}
          </button>
          <Badge variant="secondary" className="text-[10px]">{followupTypeLabel(f.followup_type)}</Badge>
          {f.lead && <Badge variant="outline" className="text-[10px]">{leadTypeLabel(f.lead.lead_type)} lead</Badge>}
          <Badge variant="outline" className={`text-[10px] ${priorityClass(f.priority)}`}>
            {f.priority === "high" && <Flame className="mr-0.5 h-2.5 w-2.5" />}{priorityLabel(f.priority)}
          </Badge>
          {statusBadge()}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {f.status === "completed" ? `Completed ${fmtDateTime(f.completed_at)}` : `Due ${fmtDateTime(f.due_at)}`}
          {f.note ? ` · ${f.note}` : ""}
        </p>
      </div>

      {pending && (
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={busy} data-testid={`followup-complete-${f.id}`}>
            <CheckCircle2 className="h-4 w-4" /> Complete
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-md border border-border p-1.5 hover:bg-secondary" data-testid={`followup-actions-${f.id}`} aria-label="Follow-up actions">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => reschedule(isoIn(1))} data-testid={`followup-snooze-tomorrow-${f.id}`}>Reschedule · Tomorrow</DropdownMenuItem>
              <DropdownMenuItem onClick={() => reschedule(isoIn(3))} data-testid={`followup-snooze-3d-${f.id}`}>Reschedule · 3 days</DropdownMenuItem>
              <DropdownMenuItem onClick={() => reschedule(isoIn(7))} data-testid={`followup-snooze-1w-${f.id}`}>Reschedule · 1 week</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setCustomDue(""); setCustomOpen(true); }} data-testid={`followup-snooze-custom-${f.id}`}>Reschedule · Choose date…</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={cancel} className="text-destructive" data-testid={`followup-cancel-${f.id}`}>
                <XCircle className="h-4 w-4" /> Cancel follow-up
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Did you connect? */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm" data-testid="followup-connected-dialog">
          <DialogHeader>
            <DialogTitle>Did you connect with this contact?</DialogTitle>
            <DialogDescription>We'll only update “last contacted” if you actually reached them.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => complete(false)} disabled={busy} data-testid="followup-connected-no">No, just complete</Button>
            <Button onClick={() => complete(true)} disabled={busy} data-testid="followup-connected-yes">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, we connected"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom reschedule */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-w-sm" data-testid="followup-reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule follow-up</DialogTitle>
            <DialogDescription>Pick a new date and time.</DialogDescription>
          </DialogHeader>
          <Input type="datetime-local" value={customDue} onChange={(e) => setCustomDue(e.target.value)} data-testid="followup-reschedule-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button onClick={() => customDue && reschedule(new Date(customDue).toISOString())} disabled={busy || !customDue} data-testid="followup-reschedule-save">Reschedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
